import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Checkbox,
  SimpleGrid,
  SimpleGridProps,
} from "@chakra-ui/react";
import React, { ReactNode } from "react";
import { PropsWithChildren } from "react";

import { QuickRowAction, RowAction, RowActionsMenu } from "./row-actions-menu";
import { ColumnDefinition } from "./table";
import { TableCell } from "./table-cell";

interface TableRowProps<TableRowType> {
  rowData: TableRowType;
  columnDefinitions: ColumnDefinition<TableRowType>[];
  buttonVariant: string;
  hasSelection?: boolean;
  hasQuickRowAction?: boolean;
  hasRowActions?: boolean;
  selected?: boolean;
  setSelected?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  quickRowAction?: QuickRowAction<TableRowType>;
  rowActions?: RowAction<TableRowType>[];
  lineHeight?: string;
  rowAccordion?: (row: TableRowType) => ReactNode;
  gridProps?: SimpleGridProps;
  accordionButtonProps?: React.ComponentProps<typeof AccordionButton>;
}

function TableRowSelf<TableRowType>({
  rowData,
  columnDefinitions,
  hasSelection = false,
  hasQuickRowAction = false,
  hasRowActions = false,
  selected = false,
  setSelected,
  quickRowAction,
  rowActions,
  lineHeight,
  buttonVariant,
  gridProps,
}: PropsWithChildren<TableRowProps<TableRowType>>) {
  return (
    <SimpleGrid {...gridProps}>
      {hasQuickRowAction && quickRowAction ? (
        <TableCell
          display="flex"
          alignItems="center"
          lineHeight={lineHeight}
          title={quickRowAction.title}
          onClick={() => quickRowAction.action(rowData)}
          cursor="pointer"
        >
          {quickRowAction.icon}
        </TableCell>
      ) : null}
      {hasSelection ? (
        <TableCell display="flex" alignItems="center" lineHeight={lineHeight}>
          <Checkbox margin={0} size="lg" isChecked={selected} onChange={setSelected} />
        </TableCell>
      ) : null}
      {columnDefinitions.map((column, colIdx) => {
        const content = column.renderFunction(rowData);

        let title = undefined;
        if (typeof content === "string" || typeof content === "number") title = content + "";
        if (content && typeof content === "object" && "props" in content && content.props)
          title = content.props.children;

        return (
          <TableCell
            title={title}
            lineHeight={lineHeight}
            key={`data-row-${colIdx}`}
            textAlign={column.align}
          >
            {column.renderFunction(rowData)}
          </TableCell>
        );
      })}
      {hasRowActions && rowActions ? (
        <RowActionsMenu buttonVariant={buttonVariant} row={rowData} actions={rowActions} />
      ) : null}
    </SimpleGrid>
  );
}

export function TableRow<TableRowType>(props: PropsWithChildren<TableRowProps<TableRowType>>) {
  const { rowAccordion, rowData, accordionButtonProps } = props;
  if (rowAccordion) {
    const element = rowAccordion(rowData);
    if (element) {
      return (
        <AccordionItem border="0" w="full" p={0}>
          {({ isExpanded }) => (
            <>
              <AccordionButton position="relative" display="block" p={0} {...accordionButtonProps}>
                <TableRowSelf {...props} />
                <AccordionIcon
                  position="absolute"
                  right={3}
                  top="50%"
                  transform="translateY(-50%)"
                />
              </AccordionButton>
              <AccordionPanel pb={4}>{isExpanded && element}</AccordionPanel>
            </>
          )}
        </AccordionItem>
      );
    }
  }
  return <TableRowSelf {...props} />;
}
